import React from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import type { PeopleDataProvider } from '../../utils/people-data-providers';
import { IconStack } from '../sources-stack/icon-stack';
import { providers } from './providers';

const messages = defineMessages({
	sourcedFromTeamworkGraph: {
		id: 'search-ai.common.people-data-sources.people-data-sources.sourcedFromTeamworkGraph',
		defaultMessage: 'Sourced from Teamwork Graph',
		description:
			'Attribution shown beside provider logos when an answer only has people data sources.',
	},
	peopleSourcedFromTeamworkGraph: {
		id: 'search-ai.common.people-data-sources.people-data-sources.peopleSourcedFromTeamworkGraph',
		defaultMessage: 'People information is sourced from Teamwork Graph',
		description:
			'People data attribution shown beside provider logos in the sources popup or side panel.',
	},
});

const styles = cssMap({
	footer: {
		flexShrink: 0,
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.150'),
	},
	divider: {
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
	popupFooter: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.150'),
	},
	peopleOnlySidePanelFooter: {
		paddingTop: token('space.0'),
	},
	standalone: {
		width: 'fit-content',
		maxWidth: '100%',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
	},
	standaloneRow: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.small'),
		paddingTop: token('space.100'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.150'),
	},
	row: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.medium'),
		paddingTop: token('space.075'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.100'),
		justifyContent: 'center',
	},
	logo: { display: 'inline-flex' },
});

export const PeopleDataSources = ({
	peopleDataProviders: dataProviders,
	hasSources,
	isInPopup = false,
	isStandalone = false,
}: {
	peopleDataProviders: PeopleDataProvider[];
	hasSources: boolean;
	isInPopup?: boolean;
	isStandalone?: boolean;
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	if (dataProviders.length === 0) {
		return null;
	}

	const providerLogos = (
		<Box
			role="img"
			aria-label={dataProviders.map((provider) => providers[provider].name).join(', ')}
		>
			<IconStack>
				{dataProviders.map((provider) => {
					const { name, Icon } = providers[provider];
					return (
						<Tooltip key={provider} content={name}>
							{(tooltipProps) => (
								<Box as="span" {...tooltipProps} xcss={styles.logo}>
									<Icon label="" size="xxsmall" />
								</Box>
							)}
						</Tooltip>
					);
				})}
			</IconStack>
		</Box>
	);

	return (
		<Box
			xcss={cx(
				styles.footer,
				hasSources && styles.divider,
				isInPopup && styles.popupFooter,
				!hasSources && !isInPopup && styles.peopleOnlySidePanelFooter,
				isStandalone && styles.standalone,
			)}
			testId="people-data-sources"
		>
			<Inline
				xcss={isStandalone ? styles.standaloneRow : styles.row}
				space="space.100"
				alignBlock="center"
			>
				{providerLogos}
				<Text color="color.text.subtlest" size={isStandalone ? 'medium' : 'small'}>
					{formatMessage(
						isStandalone
							? messages.sourcedFromTeamworkGraph
							: messages.peopleSourcedFromTeamworkGraph,
					)}
				</Text>
			</Inline>
		</Box>
	);
};
